import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getTeamNames } from '../../services/cricketAPI';
import '../../styles/cricket/MatchCard.css';

const MatchCard = ({ match, formatDateTime }) => {
    const navigate = useNavigate();
    
    const {
        marketid,
        matchid,
        matchName,
        cname,
        matchdate,
        inPlay,
        status,
        b1,
        l1,
        b2,
        l2,
        tv,
        f,
        rc,
        gscode
    } = match;


const isTestMatch =
    matchName === 'Test Matches' ||
    matchName === 'The Hundred - Womens' ||
    matchName === '  Caribbean Premier League' ||
    matchName === 'The Hundred';
    
    // Check if match is suspended
    const isSuspended = status === 'SUSPENDED' || status === 'SUSPENDEDx';
    const isLive = inPlay === true;
    const isOpen = status === 'OPEN';
    
    // ✅ NEW: Match is disabled if suspended OR test match
    const isDisabled = isSuspended || isTestMatch;

    const getStatusBadge = () => {
        if (isLive) {
            return <span className="badge live">LIVE</span>;
        }
        if (isOpen) {
            return <span className="badge open">OPEN</span>;
        }
        return <span className="badge suspended">SUSPENDED</span>;
    };

    // ✅ NEW: Get test match badge
    const getTestMatchBadge = () => {
        if (isTestMatch) {
            return <span className="badge test-match">COMING SOON</span>;
        }
        return null;
    };

    const { team1, team2 } = getTeamNames(matchName);

    const formatOdds = (odds) => {
        if (odds === 0 || odds === undefined) return '-';
        return odds.toFixed(2);
    };

    const handleCardClick = () => {
        // Don't navigate if disabled (suspended or test match)
        if (isDisabled) return;

        const providerMarketId = marketid ?? match.marketId;
        const providerEventId = matchid ?? match.eventId;
        if (!providerMarketId || !providerEventId) return;

        navigate(`/user/match/${encodeURIComponent(providerMarketId)}?gmid=${encodeURIComponent(providerEventId)}`);
    };

    return (
        <div className="match-card-wrapper">

            
            
            {/* =========================================================== */}
            {/* DESKTOP LAYOUT - KEPT EXACTLY AS YOU PROVIDED             */}
            {/* =========================================================== */}
            <div 
                className={`match-row desktop-layout ${isLive ? 'live-match' : ''} ${isDisabled ? 'disabled-match' : ''}`}
                onClick={handleCardClick}
                style={{ 
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    opacity: isDisabled ? 0.65 : 1 
                }}
            >
                {/* Game Info - Team names and date only */}
                <div className="game-info">
                    <span className="team-names">
                        {team1} <span className="vs-text">v</span> {team2}
                    </span>
                    <span className="match-meta">
                        <span className="date">{formatDateTime(matchdate)}</span>
                    </span>
                </div>

                {/* 1 Column - Back & Lay side by side */}
                <div className="odds-column">
                    {/* Dot sits OUTSIDE the odds-box, parent is odds-column */}
                    {isLive && <div className="live-dot"></div>}
                    
                    <div className={`odds-box back ${isDisabled ? 'disabled' : ''}`}>
                        {formatOdds(b1)}
                    </div>
                    <div className={`odds-box lay ${isDisabled ? 'disabled' : ''}`}>
                        {formatOdds(l1)}
                    </div>
                </div>

                {/* X Column - Draw odds */}
                <div className="odds-column draw-column">
                    {/* If your API sends X odds data, replace these with variables like bX and lX */}
                    <div className="odds-box back disabled">-</div>
                    <div className="odds-box lay disabled">-</div>
                </div>

                {/* 2 Column - Back & Lay side by side */}
                <div className="odds-column">
                    <div className={`odds-box back ${isDisabled ? 'disabled' : ''}`}>
                        {formatOdds(b2)}
                    </div>
                    <div className={`odds-box lay ${isDisabled ? 'disabled' : ''}`}>
                        {formatOdds(l2)}
                    </div>
                </div>

             
            </div>

            {/* =========================================================== */}
            {/* MOBILE LAYOUT - ALL 6 ODDS BOXES (EQUAL SIZED 1 X 2)       */}
            {/* =========================================================== */}
            <div 
                className={`match-row mobile-layout ${isDisabled ? 'disabled-match' : ''}`}
                onClick={handleCardClick}
            >
                {/* 1. TOP ROW: Team Names (Left) + Live Dot (Right) */}
                <div className="mobile-top-row">
                    <span className="mobile-team-names">
                        {team1} v {team2}
                    </span>
                    <div className="mobile-live-dot">
                        {isLive && (
                            <>
                           
                                <div className="dot"></div>
                            </>
                        )}
                       
                    </div>
                </div>

                {/* 2. MIDDLE ROW: Date */}
                <div className="mobile-date-row">
                    <span className="mobile-date">{formatDateTime(matchdate)}</span>
                </div>

                {/* 3. BOTTOM ROW: 1 X 2 Header + Odds Grid */}
                {!isTestMatch && (
                    <div className="mobile-odds-wrapper">
                        {/* HEADER: 1 | X | 2 */}
                        <div className="mobile-odds-header">
                            <div>1</div>
                            <div>X</div>
                            <div>2</div>
                        </div>

                        {/* ODDS ROW - 3 Columns of EQUAL size */}
                        <div className="mobile-odds-row">
                            
                            {/* Column 1: Team 1 Odds (Back + Lay) */}
                            <div className="mobile-odds-col">
                                <div className={`mobile-odds-box back ${isDisabled ? 'disabled' : ''}`}>
                                    {formatOdds(b1)}
                                </div>
                                <div className={`mobile-odds-box lay ${isDisabled ? 'disabled' : ''}`}>
                                    {formatOdds(l1)}
                                </div>
                            </div>

                            {/* Column 2: X / Draw Odds (Back + Lay) */}
                            <div className="mobile-odds-col">
                                <div className={`mobile-odds-box back ${isDisabled ? 'disabled' : ''}`}>
                                    - {/* Replace with your X Back Odds variable */}
                                </div>
                                <div className={`mobile-odds-box lay ${isDisabled ? 'disabled' : ''}`}>
                                    - {/* Replace with your X Lay Odds variable */}
                                </div>
                            </div>

                            {/* Column 3: Team 2 Odds (Back + Lay) */}
                            <div className="mobile-odds-col">
                                <div className={`mobile-odds-box back ${isDisabled ? 'disabled' : ''}`}>
                                    {formatOdds(b2)}
                                </div>
                                <div className={`mobile-odds-box lay ${isDisabled ? 'disabled' : ''}`}>
                                    {formatOdds(l2)}
                                </div>
                            </div>

                        </div>
                    </div>
                )}


            </div>

        </div>
    );
};

export default MatchCard;